/**
 * WebhookEndpointTable - lists registered outbound webhook endpoints.
 *
 * Columns: Name, URL, Subscribed events (chips), Status (Active toggle),
 * Last success / Last failure, Actions (Delete).
 *
 * The "Active" toggle calls `onToggleActive(endpoint, nextValue)` and
 * the parent runs the PATCH. Delete is gated by an inline confirm
 * dialog.
 */
import { useState } from "react";

import { Badge, Button } from "@/components/ui";
import type { WebhookEndpointResponse } from "@/types/api";

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString();
}

export interface WebhookEndpointTableProps {
  endpoints: WebhookEndpointResponse[];
  onToggleActive: (
    endpoint: WebhookEndpointResponse,
    nextValue: boolean,
  ) => void;
  onDelete: (endpointId: string) => void;
  isMutating?: boolean;
}

export function WebhookEndpointTable({
  endpoints,
  onToggleActive,
  onDelete,
  isMutating = false,
}: WebhookEndpointTableProps): JSX.Element {
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  if (endpoints.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center">
        <i
          className="fa-duotone fa-thin fa-bolt text-3xl text-neutral-400"
          aria-hidden="true"
        />
        <p className="mt-3 text-sm font-medium text-neutral-900">
          No webhook endpoints registered
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Add one to receive HTTP POSTs when tickets change.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Name
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                URL
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Events
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Status
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Last success
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Last failure
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {endpoints.map((ep) => (
              <tr key={ep.id} className="hover:bg-neutral-50">
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                  {ep.name}
                  {ep.description && (
                    <p className="mt-0.5 text-xs font-normal text-neutral-500">
                      {ep.description}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-700">
                  <span className="break-all">{ep.url}</span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {ep.event_types.length === 0 ? (
                    <Badge variant="info">All events</Badge>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {ep.event_types.map((evt) => (
                        <Badge key={evt} variant="default">
                          {evt}
                        </Badge>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ep.is_active}
                      onChange={(e) =>
                        onToggleActive(ep, e.target.checked)
                      }
                      disabled={isMutating}
                      className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      aria-label={`Toggle ${ep.name} active`}
                    />
                    {ep.is_active ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="default">Inactive</Badge>
                    )}
                  </label>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-500">
                  {formatDate(ep.last_success_at)}
                </td>
                <td className="px-4 py-3 text-xs text-neutral-500">
                  {formatDate(ep.last_failure_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPendingDeleteId(ep.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pendingDeleteId && (
        <DeleteEndpointConfirmDialog
          isOpen
          isLoading={isMutating}
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => {
            const id = pendingDeleteId;
            setPendingDeleteId(null);
            onDelete(id);
          }}
        />
      )}
    </>
  );
}

interface DeleteEndpointConfirmDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteEndpointConfirmDialog({
  isOpen,
  isLoading,
  onCancel,
  onConfirm,
}: DeleteEndpointConfirmDialogProps): JSX.Element | null {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCancel}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-neutral-900">
          Delete this webhook endpoint?
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          The endpoint will no longer receive any events. In-flight retries
          will be aborted. This action cannot be undone.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={onConfirm}
            isLoading={isLoading}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WebhookEndpointTable;
